import React from 'react';
import { ApartmentOutlined } from '@ant-design/icons';
import { Collapse, Space, Tag, Typography } from 'antd';
import type { JobStatus, UnitTypeView } from '../types';
import { jobStatusMeta } from '../utils';
import { ClosetAccordion } from './ClosetAccordion';

interface UnitTypeBlockProps {
  typeView: UnitTypeView;
  projectIdForUnits: string;
  postMeasure?: boolean;
}

export const UnitTypeBlock: React.FC<UnitTypeBlockProps> = ({
  typeView,
  projectIdForUnits,
  postMeasure,
}) => {
  // Aggregate job status counts for the unit type header
  const typeJobCounts = typeView.unitNumbers.reduce(
    (acc, unitView) => {
      // Count statuses from closets directly
      unitView.closets.forEach((closet) => {
        const status = closet.state; // 'new' | 'pending' | 'failed' | 'done' | 'unknown'
        // Map closet state to job status keys if possible, or just use the state directly
        // jobStatusMeta keys: new, pending, failed.
        // closet states: new, pending, failed, done, unknown.
        if (status === 'done') {
          acc['done'] = (acc['done'] || 0) + 1;
        } else if (status === 'failed') {
          acc['failed'] = (acc['failed'] || 0) + 1;
        } else if (status === 'pending') {
          acc['pending'] = (acc['pending'] || 0) + 1;
        } else if (status === 'new') {
          acc['new'] = (acc['new'] || 0) + 1;
        }
        acc['total'] = (acc['total'] || 0) + 1;
      });
      return acc;
    },
    {} as Record<string, number>,
  );

  const header = (
    <div className='unit-type-block__header' style={{ marginBottom: 0, paddingBottom: 0 }}>
      <Typography.Text strong>{typeView.unitType.name || 'Unit Type'}</Typography.Text>
      <Space size={[6, 6]} wrap style={{ marginLeft: 12 }}>
        {typeof typeView.unitType.numberOfUnits === 'number' && (
          <Tag
            icon={<ApartmentOutlined />}
            className='project-panel__tag project-panel__tag--muted'
          >
            {typeView.unitType.numberOfUnits.toLocaleString()} Units
          </Tag>
        )}
        {(Object.keys(jobStatusMeta) as JobStatus[]).map((status) => {
          const meta = jobStatusMeta[status];
          const count = typeJobCounts[status] || 0;
          // User wants all labels shown
          return (
            <Tag
              key={status}
              color={meta.color}
              icon={meta.icon}
              className='project-panel__status-tag'
            >
              {meta.label}: {count.toLocaleString()}
            </Tag>
          );
        })}
        <Tag
          color='#52c41a'
          icon={<ApartmentOutlined />} // Or CheckCircleOutlined if imported
          className='project-panel__status-tag'
        >
          Done: {(typeJobCounts['done'] || 0).toLocaleString()} / {(typeJobCounts['total'] || 0).toLocaleString()}
        </Tag>
      </Space>
    </div>
  );

  const content = postMeasure ? (
    <div className='unit-type-block__units'>
      {typeView.unitNumbers.map((unitView) => (
        <div key={unitView.unitNumber.id ?? unitView.unitNumber.name} style={{ marginBottom: 4 }}>
          {/* For postMeasure=true, we want to see unit number and closets immediately. 
               UnitCard uses ClosetAccordion which is collapsible. 
               We can pass a prop to UnitCard to make it open by default or just render closets directly with a header.
               Let's try to render closets directly but grouped by unit number.
           */}
          <div className='unit-card-header' style={{ marginBottom: 4, fontWeight: 'bold' }}>
            {unitView.unitNumber.name || 'Unit'}
            {unitView.unitNumber.floor && (
              <span style={{ fontWeight: 'normal', color: '#888', marginLeft: 8 }}>
                Floor {unitView.unitNumber.floor}
              </span>
            )}
          </div>
          <ClosetAccordion
            closets={unitView.closets}
            projectIdForUnits={projectIdForUnits}
            defaultOpen
          />
        </div>
      ))}
    </div>
  ) : (
    <div className='unit-type-block__closets'>
      {typeView.unitNumbers.length > 0 && (
        <ClosetAccordion
          closets={typeView.unitNumbers[0].closets}
          projectIdForUnits={projectIdForUnits}
          defaultOpen
        />
      )}
    </div>
  );

  return (
    <div className='unit-type-block'>
      <Collapse
        size='small'
        className='unit-type-block__collapse'
        items={[
          {
            key: typeView.unitType.id ?? typeView.unitType.name ?? '1',
            label: header,
            children: content,
          },
        ]}
      />
    </div>
  );
};
